import { isValidTagSlug, normalizeTagLabel, TagValidationError } from '../../tags';
import type { Revision, RevisionTag, RevisionTags } from '../../types';
import { addUserEdits, isYValue, jsonToY, mutations } from '../../utils';

/**
 * Get a tag in a revision by its slug.
 */
export function getTagInRevision(
    revision: mutations.MutableData<Revision>,
    slug: string
): mutations.MutableData<RevisionTag> | undefined {
    const tags = mutations.get(revision, 'tags');
    if (!tags) {
        return undefined;
    }
    return mutations.get(tags, slug);
}

/**
 * Insert a tag in a revision (keyed by slug).
 */
export function insertTagInRevision(
    revision: mutations.MutableData<Revision>,
    tag: RevisionTag
): void {
    if (!isValidTagSlug(tag.slug)) {
        throw new TagValidationError(`Invalid tag slug "${tag.slug}"`);
    }
    const candidate = { ...tag, label: normalizeTagLabel(tag.label) };
    if (!candidate.label) {
        throw new TagValidationError(`Tag "${tag.slug}" needs a non-empty label`);
    }
    let tags = mutations.get(revision, 'tags');
    if (tags && mutations.has(tags, tag.slug)) {
        throw new TagValidationError(`Tag "${tag.slug}" already exists`);
    }
    if (!tags) {
        tags = (isYValue(revision) ? jsonToY({}) : {}) as mutations.MutableData<RevisionTags>;
        mutations.set(revision, 'tags', tags);
    }
    mutations.set(tags, tag.slug, candidate);
}

/**
 * Remove a tag from a revision by slug.
 */
export function removeTagInRevision(
    revision: mutations.MutableData<Revision>,
    slug: string,
    _authors: string[] = []
): void {
    const tags = mutations.get(revision, 'tags');
    if (tags) {
        mutations.del(tags, slug);
    }
}

/**
 * Update a tag in a revision by slug.
 */
export function updateTagInRevision(
    revision: mutations.MutableData<Revision>,
    slug: string,
    update: Partial<Omit<RevisionTag, 'slug' | 'icon' | 'emoji'>> & {
        icon?: string | null;
        emoji?: string | null;
    },
    authors: string[] = []
): void {
    const existing = getTagInRevision(revision, slug);
    if (!existing) {
        throw new TagValidationError(`Tag "${slug}" does not exist`);
    }
    const attributes = { ...update };
    if (attributes.label !== undefined) {
        attributes.label = normalizeTagLabel(attributes.label);
        if (!attributes.label) {
            throw new TagValidationError(`Tag "${slug}" needs a non-empty label`);
        }
    }
    if (attributes.icon != null) {
        attributes.emoji = null;
    } else if (attributes.emoji != null) {
        attributes.icon = null;
    }
    Object.entries(attributes).forEach(([key, value]) => {
        if (value === null || value === undefined) {
            mutations.del(existing, key as keyof RevisionTag);
        } else {
            mutations.set(
                existing,
                // @ts-ignore
                key,
                // @ts-ignore
                isYValue(revision) ? jsonToY(value) : value
            );
        }
    });
    if (authors.length) {
        addUserEdits(mutations.get(existing, 'edits'), authors);
    }
}
